/**
 * LandingPage - Pagină publică de prezentare e-Tesa
 * Optimizată SEO, indexabilă de Google. Login mutat la /login.
 */
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ETESA_OPERATOR as OP } from "@/config/operator";
import {
  Calculator, Clock, FileText, Car, Building2, Sparkles, Users, ShieldCheck,
  Phone, Mail, ArrowRight, Check, Layers, Receipt, ChevronRight
} from "lucide-react";

const features = [
  {
    icon: Clock,
    title: "Pontaj digital",
    desc: "Calendar pontaj cu validare GPS pe șantier. Cereri concediu, sărbători legale calculate automat.",
  },
  {
    icon: Calculator,
    title: "Devize cu AI (DevizPRO)",
    desc: "Generează devize pe baza unei descrieri text. AI-ul învață din proiectele tale și sugerează prețuri reale.",
  },
  {
    icon: Receipt,
    title: "Facturi & e-Factura ANAF",
    desc: "Emite facturi pe baza devizelor. Import automat date beneficiar din ANAF prin CUI.",
  },
  {
    icon: FileText,
    title: "Contracte WYSIWYG",
    desc: "Antrepriză, subantrepriză, prestări servicii. Editor vizual A4 cu semnături și anexe.",
  },
  {
    icon: Car,
    title: "Foi de parcurs",
    desc: "Kilometraj, ITP, RCA, alerte la expirare. Export Excel lunar pentru contabilitate.",
  },
  {
    icon: Building2,
    title: "Proiecte & șantiere",
    desc: "Pipeline cu faze, echipe alocate, utilaje. Raport proiect cu profitabilitate reală.",
  },
];

const flows = [
  { num: "01", title: "Pontează echipa", text: "Angajații se pontează pe șantier cu un click din aplicația mobilă (PWA)." },
  { num: "02", title: "Generează devize", text: "Cere ofertă din text liber — AI-ul propune articole cu cantități și prețuri." },
  { num: "03", title: "Trimite contract & factură", text: "Din deviz, generezi contract + factură + procese verbale legate automat." },
  { num: "04", title: "Raport profitabilitate", text: "Vezi în timp real costurile reale vs estimate per proiect/șantier." },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("user") || sessionStorage.getItem("user");
      if (raw) {
        const u = JSON.parse(raw);
        setIsLoggedIn(true);
        setUserRole(u.role);
      }
    } catch {
      /* noop */
    }
    // SEO: forțează canonical-ul homepage (pentru cazul când userul navighează din /blog → /)
    document.title = "e-Tesa | Gestiune Pontaj, Devize și Șantier pentru Firme de Construcții";
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://e-tesa.ro/");
    const ogImg = document.querySelector('meta[property="og:image"]');
    if (ogImg) ogImg.setAttribute("content", "https://e-tesa.ro/og/homepage.jpg");
  }, []);

  const goToDashboard = () => {
    if (userRole === "superadmin") navigate("/superadmin");
    else if (userRole === "master" || userRole === "admin") navigate("/master");
    else if (userRole === "client") navigate("/client");
    else navigate("/worker");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50/30 to-blue-50/40 text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#functii" className="hover:text-teal-600 transition-colors">Funcții</a>
            <a href="#flux" className="hover:text-teal-600 transition-colors">Cum funcționează</a>
            <Link to="/blog" className="hover:text-teal-600 transition-colors">Blog</Link>
            <a href="#contact" className="hover:text-teal-600 transition-colors">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <Button
                onClick={goToDashboard}
                className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white rounded-xl"
                data-testid="landing-go-dashboard"
              >
                Mergi în cont
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/login")}
                  className="text-slate-700 hover:bg-slate-100 rounded-xl"
                  data-testid="landing-login"
                >
                  Intră în cont
                </Button>
                <Button
                  onClick={() => navigate("/login?register=1")}
                  className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white rounded-xl"
                  data-testid="landing-register"
                >
                  Cont nou
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-100/70 text-teal-700 rounded-full text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Cu DevizPRO AI integrat
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
              Personalul tău <span className="bg-gradient-to-r from-teal-500 to-blue-500 bg-clip-text text-transparent">TESA</span> în format digital
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
              Pontaj, devize, facturi, contracte, foi de parcurs și proiecte — toate într-o singură platformă, gândită pentru firmele de construcții din România.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() => navigate("/login?register=1")}
                className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-600 hover:to-blue-600 text-white rounded-xl px-6 h-12 shadow-lg shadow-teal-500/20"
                data-testid="landing-hero-cta"
              >
                Începe gratuit — 200 credite bonus
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById("flux")?.scrollIntoView({ behavior: "smooth" })}
                className="rounded-xl px-6 h-12 border-slate-300 hover:bg-slate-50"
              >
                Vezi cum funcționează
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 pt-2">
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> Fără card de credit</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> Funcționează pe telefon (PWA)</span>
              <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> e-Factura ANAF</span>
            </div>

            {/* Verified Operator Badge — link rapid la pagina /despre */}
            <Link
              to="/despre"
              data-testid="verified-operator-badge"
              className="inline-flex items-center gap-2.5 mt-4 px-3 py-2 rounded-xl bg-white/70 backdrop-blur border border-emerald-200 hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 transition-all group"
            >
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-sm">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Operator verificat</span>
                <div className="flex items-center gap-2.5 text-[11px] text-slate-600">
                  <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> GDPR</span>
                  <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> ANPC</span>
                  <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> ANAF</span>
                </div>
              </div>
              <span className="text-[10px] text-emerald-600 group-hover:translate-x-0.5 transition-transform ml-1">→</span>
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-teal-500/10 via-blue-500/10 to-purple-500/10 border border-white/60 backdrop-blur-xl p-8 shadow-2xl shadow-teal-500/10">
              <div className="grid grid-cols-2 gap-4 h-full">
                {features.slice(0, 4).map((f) => (
                  <div key={f.title} className="bg-white/70 backdrop-blur rounded-2xl p-4 border border-white/80 flex flex-col">
                    <f.icon className="w-7 h-7 text-teal-600 mb-2" />
                    <div className="font-semibold text-sm">{f.title}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-3">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="functii" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Tot ce ai nevoie pentru o firmă de construcții</h2>
          <p className="text-slate-600">Înlocuiește 6 aplicații cu una singură. Datele rămân conectate între ele.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f) => (
            <div key={f.title} className="bg-white/70 backdrop-blur rounded-2xl p-6 border border-white/80 hover:shadow-xl hover:shadow-teal-500/5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-500/15 to-blue-500/15 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-teal-600" />
              </div>
              <h3 className="font-semibold mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Flow */}
      <section id="flux" className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Un flux simplu, de la șantier la profit</h2>
            <p className="text-slate-400">Datele introduse o dată curg automat în toate documentele.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {flows.map((flow) => (
              <div key={flow.num} className="relative">
                <div className="text-5xl font-bold bg-gradient-to-br from-teal-400 to-blue-400 bg-clip-text text-transparent mb-3">
                  {flow.num}
                </div>
                <h3 className="font-semibold text-lg mb-2">{flow.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{flow.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — eliminat temporar (varianta nu e finală) */}

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6 text-center">
          <div className="p-6">
            <ShieldCheck className="w-10 h-10 mx-auto text-teal-600 mb-3" />
            <h3 className="font-semibold mb-1">GDPR compliant</h3>
            <p className="text-sm text-slate-600">Pagini privacy & terms, cookie banner, export & ștergere date la cerere.</p>
          </div>
          <div className="p-6">
            <Layers className="w-10 h-10 mx-auto text-teal-600 mb-3" />
            <h3 className="font-semibold mb-1">Multi-tenant securizat</h3>
            <p className="text-sm text-slate-600">Datele firmei tale sunt izolate complet. Zero acces între firme.</p>
          </div>
          <div className="p-6">
            <Users className="w-10 h-10 mx-auto text-teal-600 mb-3" />
            <h3 className="font-semibold mb-1">Suport în română</h3>
            <p className="text-sm text-slate-600">Chat AI direct în aplicație + telefon și email pentru cazuri complexe.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-teal-500 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Gata să digitalizezi firma?</h2>
          <p className="text-teal-50 mb-8 max-w-xl mx-auto">
            Deschide un cont în mai puțin de un minut. 200 credite bonus de bun-venit te ajută să probezi toate funcțiile fără să plătești.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/login?register=1")}
            className="bg-white text-teal-700 hover:bg-slate-100 rounded-xl px-8 h-12 font-semibold shadow-xl"
            data-testid="landing-final-cta"
          >
            Începe acum, gratuit
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <img src="/etesa-logo.png" alt="e-Tesa" className="h-10 w-auto mb-3" />
            <p className="text-sm text-slate-400 leading-relaxed">
              Platforma all-in-one pentru gestionarea firmelor de construcții din România.
            </p>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Operator platformă</h4>
            <p className="text-sm text-slate-400">{OP.legal_name}</p>
            <p className="text-xs text-slate-500 mt-1">CUI: {OP.cui}</p>
            <p className="text-xs text-slate-500">Reg. Com.: {OP.reg_com}</p>
            <p className="text-xs text-slate-500 mt-1">{OP.address_full}</p>
            <Link to="/despre" className="text-xs text-teal-400 hover:underline mt-2 inline-block" data-testid="landing-footer-about">
              Vezi date complete →
            </Link>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Contact</h4>
            <a href={`tel:${OP.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-2">
              <Phone className="w-4 h-4" /> {OP.phone}
            </a>
            <a href={`mailto:${OP.email}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
              <Mail className="w-4 h-4" /> {OP.email}
            </a>
            <a href={OP.anpc_sol_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline mt-3 inline-block">
              ANPC SOL ↗
            </a>
          </div>
          <div>
            <h4 className="font-semibold text-white mb-3">Resurse</h4>
            <Link to="/blog" className="block text-sm text-slate-400 hover:text-white transition-colors mb-2">Blog</Link>
            <Link to="/legal?tab=privacy" className="block text-sm text-slate-400 hover:text-white transition-colors mb-2">Confidențialitate (GDPR)</Link>
            <Link to="/legal?tab=terms" className="block text-sm text-slate-400 hover:text-white transition-colors mb-2">Termeni și Condiții</Link>
            <Link to="/legal?tab=cancellation" className="block text-sm text-slate-400 hover:text-white transition-colors">Anulare / Retragere</Link>
          </div>
        </div>
        <div className="border-t border-slate-800 py-5 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {OP.legal_name}. Toate drepturile rezervate. Platformă livrată de e-Tesa.ro.
        </div>
      </footer>
    </div>
  );
}
